import type { StepOptions } from 'shepherd.js'
import { waitForSelector } from './wait-for-selector'

/**
 * Default step options applied to every tour via `defaultStepOptions` on the
 * Shepherd.Tour. Two cooperating callbacks replace the provider's old tour-wide
 * pre-flight wait:
 *
 *   - beforeShowPromise: waits up to 1.5s for THIS step's anchor to mount.
 *     Each step waits only for its own anchor — no cross-step coupling.
 *   - showOn: if the anchor is still missing at show time, skip the step.
 *     Shepherd's next() / start() will find the next showable step or
 *     complete the tour. No "rewrite Next button to Finish" patch needed.
 *
 * Steps without `attachTo`, or whose `attachTo.element` is a function (lazy
 * resolution managed by the step author), bypass both callbacks and always
 * show. The step author owns timing in that case.
 *
 * `this` binding: Shepherd v15 invokes these as `step.options.showOn()` /
 * `step.options.beforeShowPromise()` — so inside each callback `this` IS the
 * merged options object (NOT the Step instance, and NOT the defaults POJO
 * alone). Reading `this.attachTo` gives the step's anchor. Reading
 * `this.options.…` would crash because `this.options` is undefined.
 * Verified against `node_modules/shepherd.js/dist/js/shepherd.mjs`.
 *
 * Spotlight padding/radius + smooth scrollTo + the X close icon live here
 * because they should be identical across tours.
 */
export const tourDefaultStepOptions: StepOptions = {
  scrollTo: { behavior: 'smooth', block: 'center' },
  // Contextual cancel label — assistive tech announces "Dismiss tour" instead
  // of Shepherd's default "Close Tour", matching our `tour_dismissed` event
  // naming so support / analytics terminology stays consistent.
  cancelIcon: { enabled: true, label: 'Dismiss tour' },
  modalOverlayOpeningPadding: 6,
  modalOverlayOpeningRadius: 8,

  showOn(this: StepOptions) {
    const sel = this.attachTo?.element
    if (typeof sel !== 'string') {
      return true
    }
    return document.querySelector(sel) !== null
  },

  beforeShowPromise(this: StepOptions) {
    const sel = this.attachTo?.element
    if (typeof sel !== 'string') {
      return Promise.resolve()
    }
    return waitForSelector(sel)
  },
}
